// FILE: components/PasswordInput.tsx
// A specialized input component for password fields with visibility toggle

import React, { useState } from 'react';
import { Icon } from './icons';

interface PasswordInputProps {
  id: string;
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  className?: string;
  autoComplete?: string;
  required?: boolean;
}

export const PasswordInput: React.FC<PasswordInputProps> = ({
  id,
  label,
  value,
  onChange,
  placeholder = "Enter password",
  disabled = false,
  error = "",
  className = "",
  autoComplete = "current-password",
  required = false,
}) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const baseClasses = "w-full h-14 px-4 bg-bg-tertiary dark:bg-dark-bg-primary border rounded-lg focus:outline-none focus:ring-0 focus:border-2 text-text-primary dark:text-dark-text-primary";
  const defaultBorder = "border-border-color dark:border-dark-border-color focus:border-brand-navy dark:focus:border-dark-brand-orange";
  const errorBorder = "border-2 border-system-error focus:border-system-error";
  const borderClasses = error ? errorBorder : defaultBorder;

  return (
    <div className={className}>
      {label && (
        <label htmlFor={id} className="block text-sm font-semibold text-text-primary dark:text-dark-text-primary mb-2">
          {label}
          {required && <span className="text-system-error ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          id={id}
          type={isPasswordVisible ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          disabled={disabled}
          required={required}
          className={`${baseClasses} ${borderClasses} pr-16 ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        />
        
        {/* Password Toggle Button */}
        <button
          type="button"
          onClick={togglePasswordVisibility}
          disabled={disabled}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-text-secondary/80 hover:text-text-secondary hover:bg-bg-tertiary dark:hover:bg-dark-bg-primary transition-colors focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={isPasswordVisible ? "Hide password" : "Show password"}
        >
          <Icon className="!text-xl">
            {isPasswordVisible ? 'visibility_off' : 'visibility'}
          </Icon>
        </button>
      </div>
      
      {error && (
        <p className="text-sm text-system-error mt-1 flex items-center gap-1">
          <Icon className="!text-sm">error</Icon>
          {error}
        </p>
      )}
    </div>
  );
};
